import axios from 'axios';
import { Task, CreateTaskRequest } from '../types/task';
import { jwtDecode } from 'jwt-decode';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Token management utilities
const TOKEN_REFRESH_THRESHOLD = 10 * 60; // 10 minutes in seconds

interface JwtToken {
    user_id: number;
    email: string;
    exp: number;
    iat: number;
}

const getToken = (): string | null => {
    return localStorage.getItem('token');
};

const isTokenExpired = (token: string): boolean => {
    try {
        const decoded = jwtDecode<JwtToken>(token);
        // Check if token will expire in the next 10 minutes
        return decoded.exp < (Date.now() / 1000 + TOKEN_REFRESH_THRESHOLD);
    } catch (error) {
        return true;
    }
};

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add authentication interceptor
api.interceptors.request.use(async (config) => {
    const token = getToken();
    
    if (token) {
        if (isTokenExpired(token)) {
            // If token is about to expire, try to refresh it silently
            try {
                const storedEmail = localStorage.getItem('userEmail');
                const storedPassword = localStorage.getItem('userPassword');
                
                // Only attempt refresh if credentials are stored
                if (storedEmail && storedPassword) {
                    await AuthService.login(storedEmail, storedPassword);
                    // If successful, get the new token
                    const newToken = getToken();
                    if (newToken) {
                        config.headers.Authorization = `Bearer ${newToken}`;
                        return config;
                    }
                }
                
                // If we can't refresh automatically, we'll redirect on response
            } catch (error) {
                console.error('Token refresh failed:', error);
                // Let the request go through and handle 401 in response interceptor
            }
        }
        
        config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
});

// Add response interceptor to handle unauthorized errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            console.log('Unauthorized: Redirecting to login');
            // Redirect to login page
            AuthService.logout();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export interface Category {
    id: number;
    name: string;
    color: string;
}

export interface CreateCategoryRequest {
    name: string;
    color: string;
}

export const CategoryService = {
    getCategories: async (): Promise<Category[]> => {
        const response = await api.get('/categories');
        return response.data;
    },

    createCategory: async (category: CreateCategoryRequest): Promise<Category> => {
        const response = await api.post('/categories', category);
        return response.data;
    },

    updateCategory: async (id: number, category: Partial<Category>): Promise<Category> => {
        const response = await api.put(`/categories/${id}`, category);
        return response.data;
    },

    deleteCategory: async (id: number): Promise<void> => {
        await api.delete(`/categories/${id}`);
    },
};

export const TaskService = {
    getTasks: async (): Promise<Task[]> => {
        const response = await api.get('/tasks');
        return response.data;
    },

    getTask: async (id: number): Promise<Task> => {
        const response = await api.get(`/tasks/${id}`);
        return response.data;
    },

    createTask: async (task: CreateTaskRequest): Promise<Task> => {
        const response = await api.post('/tasks', task);
        return response.data;
    },

    updateTask: async (id: number, task: Partial<Task>): Promise<Task> => {
        const response = await api.put(`/tasks/${id}`, task);
        return response.data;
    },

    deleteTask: async (id: number): Promise<void> => {
        await api.delete(`/tasks/${id}`);
    },
};

export const AuthService = {
    login: async (email: string, password: string, rememberMe: boolean = false) => {
        const response = await api.post('/auth/login', { email, password, rememberMe });
        const { token } = response.data;
        localStorage.setItem('token', token);
        
        // Store credentials for auto-refresh if remember me is checked
        if (rememberMe) {
            localStorage.setItem('userEmail', email);
            localStorage.setItem('userPassword', password);
        } else {
            localStorage.removeItem('userEmail');
            localStorage.removeItem('userPassword');
        }
        
        return response.data;
    },

    register: async (email: string, password: string, name: string) => {
        const response = await api.post('/auth/register', { email, password, name });
        const { token } = response.data;
        localStorage.setItem('token', token);
        return response.data;
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userPassword');
        window.location.href = '/login';
    },
}; 